import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useParams } from 'react-router-dom';
import '../styles/Vendite.css';
import { formatPrice } from '../utils/formatterUtils';

function Vendite() {
  const [vendite, setVendite] = useState([]);
  const [sortOption, setSortOption] = useState('name');
  const [formData, setFormData] = useState({
    alloggio: '',
    scala: '',
    piano: '',
    locali: '',
    mq_commerciali: '',
    prezzo_mq: '',
    listino_alloggio: '',
    sconto_alloggio: '',
    listino_box: '',
    sconto_box: '',
    prezzo_vendita_alloggio: '',
    varianti_alloggio: '',
    cliente: '',
    cliente_box: '',
    realizzo_box: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [cantiereId, setCantiereId] = useState(null);

  const { id } = useParams();

  const sortVendite = (data, option) => {
    return [...data].sort((a, b) => {
      if (option === 'floor') {
        const parseFloor = (p) => {
          const floor = String(p || '').trim().toUpperCase();
          const normalized = floor.replace(/[.\s]/g, '');
          if (['T', 'PT', 'PIANOTERRA'].includes(normalized)) return -1000;

          const match = floor.match(/-?\d+/);
          return match ? parseInt(match[0], 10) : 9999;
        };
        return (
          parseFloor(a.piano) - parseFloor(b.piano) ||
          a.alloggio.localeCompare(b.alloggio, 'it', { numeric: true, sensitivity: 'base' })
        );
      }
      return a.alloggio.localeCompare(b.alloggio, 'it', { numeric: true, sensitivity: 'base' });
    });
  };

  const sortedVendite = useMemo(
    () => sortVendite(vendite, sortOption),
    [vendite, sortOption]
  );

  useEffect(() => {
    setCantiereId(id);
    const fetchVendite = async () => {
      try {
        const res = await api.get(`/vendite/cantiere/${id}`);
        setVendite(res.data || []);
      } catch (err) {
        console.log('Errore nel recupero delle vendite');
        setVendite([]);
      }
    };
    fetchVendite();
  }, [id]);

  useEffect(() => {
    if (vendite.length > 0 && !isEditing) {
      setIsEditing(false);
    }
  }, [vendite, isEditing]);

  const handleChange = (e) => {
    const updatedData = {
      ...formData,
      [e.target.name]: e.target.value
    };

    const listinoAlloggio = parseFloat(updatedData.listino_alloggio) || 0;
    const scontoAlloggioPerc = parseFloat(updatedData.sconto_alloggio) || 0;
    const mqComm = parseFloat(updatedData.mq_commerciali) || 0;
    const prezzoVenditaAlloggio = listinoAlloggio * (1 - scontoAlloggioPerc / 100);
    updatedData.prezzo_vendita_alloggio = prezzoVenditaAlloggio.toFixed(2);
    updatedData.prezzo_mq = mqComm > 0 ? (prezzoVenditaAlloggio / mqComm).toFixed(2) : '';

    const listinoBox = parseFloat(updatedData.listino_box) || 0;
    const scontoBoxPerc = parseFloat(updatedData.sconto_box) || 0;
    updatedData.realizzo_box = (listinoBox * (1 - scontoBoxPerc / 100)).toFixed(2);

    setFormData(updatedData);
  };

  const handleEdit = (vendita) => {
    setFormData(vendita);
    setIsEditing(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const stato_box = formData.cliente_box?.trim() ? 'venduto' : 'disponibile';

    const payload = isEditing
      ? { ...formData, stato_box }
      : { ...formData, cantiere_id: cantiereId, stato_box };

    try {
      if (isEditing && formData.id) {
        await api.put(`/vendite/${formData.id}`, payload);
      } else {
        await api.post(`/vendite`, payload);
      }
      alert(isEditing ? 'Vendita aggiornata!' : 'Vendita aggiunta con successo!');
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert('Errore durante il salvataggio');
    }
  };

  return (
    <div className="vendite-page">
      {(!isEditing && vendite.length > 0) ? (
        <div className="vendite-cards-container">
          <div className="vendite-header d-flex justify-content-between align-items-center mb-3">
            <h3>Vendite esistenti</h3>
            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select w-auto"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="name">Ordina per nome</option>
                <option value="floor">Ordina per piano</option>
              </select>
              <button className="btn btn-success" onClick={() => {
                setFormData({
                  alloggio: '',
                  scala: '',
                  piano: '',
                  locali: '',
                  mq_commerciali: '',
                  prezzo_mq: '',
                  listino_alloggio: '',
                  sconto_alloggio: '',
                  listino_box: '',
                  sconto_box: '',
                  prezzo_vendita_alloggio: '',
                  varianti_alloggio: '',
                  cliente: '',
                  cliente_box: '',
                  realizzo_box: '',
                  cantiere_id: cantiereId
                });
                setIsEditing(true);
              }}>Aggiungi nuova vendita</button>
            </div>
          </div>
          <div className="vendite-card-grid">
            {sortedVendite.map((v) => {
              const prezzoRealeMq = v.mq_commerciali ? v.prezzo_vendita_reale_alloggio / v.mq_commerciali : 0;
              const scontoReale = ((v.prezzo_vendita_alloggio - v.prezzo_vendita_reale_alloggio) / v.prezzo_vendita_alloggio) * 100 - v.sconto_alloggio;
              const scontoClass = scontoReale > 0 ? 'worst-discount' : 'better-discount';
              return (
                <div key={v.id} className={`card vendita-card ${v.stato_alloggio === 'venduto' ? 'border-success' : ''}`}>
                  <div className="card-body">
                    <div className="vd-head">
                      <div className="vd-title">{v.alloggio}</div>
                      <span className={`vd-status ${v.stato_alloggio === 'venduto' ? 'is-sold' : 'is-available'}`}>
                        {v.stato_alloggio || 'disponibile'}
                      </span>
                    </div>

                    <div className="vd-meta">
                      <div className="vd-mi-4"><div className="mi-label">Scala</div><div className="mi-value">{v.scala}</div></div>
                      <div className="vd-mi-4"><div className="mi-label">Piano</div><div className="mi-value">{v.piano}</div></div>
                      <div className="vd-mi-4"><div className="mi-label">Locali</div><div className="mi-value">{v.locali}</div></div>
                      <div className="vd-mi-4"><div className="mi-label">MQ</div><div className="mi-value">{v.mq_commerciali}</div></div>
                    </div>

                    <div className="vd-meta-3">
                      <div className="vd-mi"><div className="mi-label">Listino Alloggio</div><div className="mi-value">{v.listino_alloggio ? Number(v.listino_alloggio).toLocaleString('it-IT', { minimumFractionDigits: 2 }) + ' €' : '—'}</div></div>
                      <div className="vd-mi"><div className="mi-label">Sconto Alloggio</div><div className="mi-value">{v.sconto_alloggio ? Number(v.sconto_alloggio).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' %' : '—'}</div></div>
                      <div className="vd-mi"><div className="mi-label">Realizzo alloggio</div><div className="mi-value">{v.prezzo_vendita_alloggio ? Number(v.prezzo_vendita_alloggio).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '—'}</div></div>
                    </div>

                    <div className="vd-meta-3">
                      <div className="vd-mi"><div className="mi-label">Prezzo €/mq</div><div className="mi-value">{Number(v.prezzo_mq || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                      <div className="vd-mi"><div className="mi-label">Vendita Reale Alloggio</div><div className="mi-value">{formatPrice(v.prezzo_vendita_reale_alloggio || 0)}</div></div>
                      <div className="vd-mi"><div className="mi-label">Prezzo reale €/mq:</div><div className="mi-value">{formatPrice(prezzoRealeMq)}</div></div>
                    </div>

                    <div className="kvchips">
                      <div className="kvchip" title={v.listino_box ? Number(v.listino_box).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '—'}>
                        <span className="chip-label"><i className="bi bi-box-seam me-1"></i> Listino box: </span>
                        <span className="chip-value">{v.listino_box ? Number(v.listino_box).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '—'}</span>
                      </div>

                      <div className="kvchip kvchip--stack" title={v.sconto_box ? Number(v.sconto_box).toLocaleString('it-IT', { minimumFractionDigits: 0 }) + ' %' : '—'}>
                        <span className="chip-label"><i className="bi bi-percent me-1"></i> Sconto box: </span>
                        <span className="chip-value">{v.sconto_box ? Number(v.sconto_box).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' %' : '—'}</span>
                      </div>

                      <div className="kvchip" title={v.realizzo_box ? Number(v.realizzo_box).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '—'}>
                        <span className="chip-label"><i className="bi bi-box2-heart me-1"></i> Realizzo box: </span>
                        <span className="chip-value">{v.realizzo_box ? Number(v.realizzo_box).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '—'}</span>
                      </div>

                      <div className="kvchip kvchip--stack" title={formatPrice(v.varianti_alloggio || 0)}>
                        <span className="chip-label"><i className="bi bi-palette me-1"></i> Varianti alloggio: </span>
                        <span className="chip-value">{formatPrice(v.varianti_alloggio || 0)}</span>
                      </div>

                      <div className="kvchip" title={v.cliente || '—'}>
                        <span className="chip-label"><i className="bi bi-person me-1"></i> Cliente: </span>
                        <span className="chip-value">{v.cliente || '—'}</span>
                      </div>

                      <div className="kvchip" title={v.cliente_box || '—'}>
                        <span className="chip-label"><i className="bi bi-person-bounding-box me-1"></i> Cliente box: </span>
                        <span className="chip-value">{v.cliente_box || '—'}</span>
                      </div>

                      <div className="kvchip" title={formatPrice(v.prezzo_vendita_reale_box || 0)}>
                        <span className="chip-label"><i className="bi bi-cash-stack me-1"></i> Vendita reale box: </span>
                        <span className="chip-value">{formatPrice(v.prezzo_vendita_reale_box || 0)}</span>
                      </div>

                      <div className="kvchip" title={`${scontoReale.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}>
                        <span className="chip-label"><i className="bi bi-activity me-1"></i> Delta Sconto (Rispetto a sconto iniziale): </span>
                        <span className={`chip-value ${scontoClass}`}>{scontoReale.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</span>
                      </div>

                    </div>
                  </div>
                  <div className="card-footer">
                    <div className="azioni-btn-container">
                      <button className="btn btn-primary rounded-btn" onClick={() => handleEdit(v)}><i className="bi bi-pencil"></i></button>
                      <button className="btn btn-sm btn-danger rounded-btn" onClick={async () => {
                        if (window.confirm('Sei sicuro di voler eliminare questa vendita?')) {
                          try {
                            await api.delete(`/vendite/${v.id}`);
                            alert('Vendita eliminata con successo!');
                            window.location.reload();
                          } catch (err) {
                            console.error(err);
                            alert("Errore durante l'eliminazione");
                          }
                        }
                      }}><i className="bi bi-x-lg"></i></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="vendite-form-container">
          <h3>{isEditing ? 'Modifica Vendita' : 'Aggiungi Vendita'}</h3>
          <form onSubmit={handleSubmit} className="row g-3">
            {Object.keys(formData).map((key) => {
              if (['prezzo_vendita_alloggio', 'prezzo_mq', 'realizzo_box', 'id', 'cantiere_id', 'stato_alloggio', 'stato_box', 'realizzo_alloggio', 'cliente', 'cliente_box', 'varianti_alloggio', 'prezzo_vendita_reale_alloggio', 'prezzo_vendita_reale_box'].includes(key)) return null;
              return (
                <div className="col-md-6" key={key}>
                  <label className="form-label">{key.replace(/_/g, ' ')}</label>
                  <input
                    type="text"
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              );
            })}

            <div className="col-12">
              <button type="submit" className="btn btn-success">{isEditing ? 'Salva' : 'Aggiungi'} Vendita</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default Vendite;
